import React, { useState, useEffect } from "react";
import { Card, Col, Row, Spinner, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import IconButton from "components/common/iconButton";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import "assets/css/site.css";
import paths from "routes/paths";
import { useTranslation } from "react-i18next";
import {getInitialPageSize,fetchRoleAndClaims,handleLoadingAndAccess,formatCreatedBy} from "assets/js/common";
import axios from "axios";
import TemplatePreviewModal from "./templatePreviewModal";
import TemplateViewModal from "./templateViewModal";
import FalconCloseButton from "components/common/falconCloseButton";
import { toast } from "react-toastify";

const Templates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sections, setSections] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // For edit and view actions
  const [editTemplateData, setEditTemplateData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTemplateData, setViewTemplateData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const language = localStorage.getItem("culture");

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "proposalTemplates",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const customerId = localStorage.getItem("customerId");
        if (!customerId) {
          setLoading(false);
          return;
        }

        const [usersRes, templatesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`, {
            params: { customerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }),
          axios.get(
            `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/proposalTemplates/${customerId}/${language}`,
            {
              headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
            }
          ),
        ]);

        const users = usersRes.data || [];
        const templates = templatesRes.data || [];

        const userMap = Object.fromEntries(users.map((u) => [u.UserId, u.Name]));

        // Sort templates by createdAt descending before mapping
        const sortedTemplates = [...templates].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        const mappedData = sortedTemplates.map((item, index) => ({
          SerialNo: index + 1,
          TemplateId: item.TemplateId,
          TemplateName: item.TemplateName,
          CreatedBy: users.find((u) => u.UserId === item.CreatedBy)?.Name || formatCreatedBy(item.CreatedBy),
          CreatedDate: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB").replace(/\//g, "-")
            : "--",
        }));

        setData(mappedData);
        setFilteredData(mappedData);

      } catch (error) {
        console.error("Error fetching templates or users:", error);
        setData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/getTemplateSections`,
          {
            params: { customerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        setSections(response.data || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
  }, []);

  const addNewTemplateToTable = (newTemplate) => {
    // Add new template to the top
    setData(prev => {
      const updated = [newTemplate, ...prev];
      return updated.sort((a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate));
    });

    setFilteredData(prev => {
      const updated = [newTemplate, ...prev];
      return updated.sort((a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate));
    });
  };

  const handleEditTemplate = async (template) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/getTemplateSectionsByTemplateId/${template.TemplateId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      const allSections = response.data || [];
      const selectedLanguage = localStorage.getItem("culture") || "nl";

      setEditTemplateData({
        ...template,
        sections: allSections,
        selectedLanguage
      });

      setShowEditModal(true);
    } catch (error) {
      console.error("Error loading template for edit:", error);
      toast.error(t("failedToLoadTemplateSections"), {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleViewTemplate = async (template) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/getTemplateSectionsByTemplateId/${template.TemplateId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      const allSections = response.data || [];
      const selectedLanguage = localStorage.getItem("culture") || "nl";

      setViewTemplateData({
        templateName: template.TemplateName,
        selectedLanguage,
        sections: allSections,
        selectionOrder: allSections.map((sec) => sec.Key),
        selectedKeys: allSections.map((sec) => sec.Key),
      });

      setShowViewModal(true);
    } catch (error) {
      console.error("Error loading template for view:", error);
      toast.error(t("failedToLoadTemplateSections"), {
        position: "top-right",
        autoClose: 3000
      });
    }
  };


  const handleDeleteClick = async (template) => {
    const customerId = localStorage.getItem("customerId");

    try {
      // Check if template is used before opening confirmation modal
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/checkTemplateUsage/${template.TemplateId}`,
        {
          params: { customerId },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        }
      );

      if (response.data.isUsed) {
        toast.error(t("templateUsedInProposal"), {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }

      setSelectedTemplate(template);
      setShowDeleteModal(true);

    } catch (error) {
      console.error("Error checking template usage:", error);
      toast.error(t("errorCheckingTemplateUsage"), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleOpenDeleteModal = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/deleteProposalTemplate/${selectedTemplate.TemplateId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      toast.success(t("templateDeleted"), {
        position: "top-right",
        autoClose: 5000,
      });

      setData((prev) =>
        prev.filter((item) => item.TemplateId !== selectedTemplate.TemplateId)
      );
      setFilteredData((prev) =>
        prev.filter((item) => item.TemplateId !== selectedTemplate.TemplateId)
      );

      setShowDeleteModal(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(t("failedToDeleteTemplate"), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    if (!globalFilter.trim()) {
      setFilteredData(data);
      return;
    }

    const lowerFilter = globalFilter.toLowerCase();
    const filtered = data.filter(
      (item) =>
        (item.TemplateName &&
          item.TemplateName.toLowerCase().includes(lowerFilter)) ||
        (item.CreatedBy &&
          item.CreatedBy.toLowerCase().includes(lowerFilter)) ||
        (item.CreatedDate &&
          item.CreatedDate.toLowerCase().includes(lowerFilter))
    );
    setFilteredData(filtered);
  }, [globalFilter, data]);

  const columns = [
    {
      accessorKey: "SerialNo",
      header: t("no"),
      cell: ({ row }) => <span>{row.index + 1}</span>,
    },
    {
      accessorKey: "TemplateName",
      header: t("templateName"),
      cell: ({ row: { original } }) => <span>{original.TemplateName}</span>,
    },
    {
      accessorKey: "CreatedBy",
      header: t("createdBy"),
      cell: ({ row: { original } }) => <span>{original.CreatedBy}</span>,
    },
    {
      accessorKey: "CreatedDate",
      header: t("createdDate"),
      cell: ({ row: { original } }) => (
        <span>{original.CreatedDate || "--"}</span>
      ),
    },
    {
      accessorKey: "Actions",
      header: t("actions"),
      cell: ({ row: { original } }) => (
        <div className="text-center">
          <IconButton
            size="sm"
            variant="primary"
            icon="pencil-alt"
            title={t("edit")}
            className="me-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEditTemplate(original);
            }}
          />
          <IconButton
            size="sm"
            variant="danger"
            icon="trash"
            title={t("delete")}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(original)
            }}
          />
        </div>
      ),
    },

  ];

  const table = useAdvanceTable({
    data: filteredData,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize("templatesTable"),
  });

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
    paths.dashboard
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect;
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background
                image={corner4}
                className="bg-card"
                style={{
                  borderTopRightRadius: "0.375rem",
                  borderBottomRightRadius: "0.375rem",
                }}
              />
              <Card.Body className="position-relative pe-4 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">{t("templatesLable")}</h5>
                  </Col>
                  <Col xs="auto">
                    <IconButton
                      onClick={() => setShowPreviewModal(true)}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      transform="shrink-3"
                    >
                      <span className="ms-1">{t("newTemplateLable")}</span>
                    </IconButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-3">
          <Card.Header>
            <Row className="flex-between-center">
              <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="p-0">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t("loading")}</span>
                </Spinner>
              </div>
            ) : (
              <>
                <AdvanceTable
                  data={filteredData}
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    size: "sm",
                    className:
                      "mb-0 overflow-hidden data-font-size table-hover",
                  }}
                  getRowProps={(row) => ({
                    onClick: () => handleViewTemplate(row.original),
                    style: { cursor: "pointer" },
                  })}
                />
                {filteredData.length === 0 && (
                  <div className="text-center pt-4">
                    {t("noDataAvailableText")}
                  </div>
                )}
              </>
            )}
          </Card.Body>

          <Card.Footer>
            <AdvanceTableFooter
              rowInfo
              navButtons
              rowsPerPageSelection
              tableKey="templatesTable"
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>

      <TemplatePreviewModal
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        refreshTemplates={addNewTemplateToTable}
        templateData={{
          templateName: "",
          selectedLanguage: language,
          selectionOrder: [],
          selectedKeys: [],
        }}
        sections={sections}
      />
      {editTemplateData && (
        <TemplatePreviewModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          refreshTemplates={addNewTemplateToTable}
          templateData={editTemplateData}
          sections={editTemplateData.sections}
        />
      )}

      {viewTemplateData && (
        <TemplateViewModal
          show={showViewModal}
          handleClose={() => setShowViewModal(false)}
          templateData={viewTemplateData}
          sections={viewTemplateData.sections}
          readOnly={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} backdrop="static">
        <Modal.Header>
          <Modal.Title>{t("confirmation")}</Modal.Title>
          <FalconCloseButton onClick={() => setShowDeleteModal(false)} />
        </Modal.Header>
        <Modal.Body>
          {t("confirmDeleteTemplate") ||
            "Are you sure you want to delete this template?"}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={() => setShowDeleteModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleConfirmDelete}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default Templates;